// Function index:
//   myWorlds()                       — worlds the user owns.
//   listWorlds()                     — all worlds (with creator).
//   getWorld(id)                     — world + hexes for the builder.
//   createWorld(body)                — new world with initialized hex grid.
//   bulkUpdateHexes(worldId, patches)— upsert many hex patches.
//   listThemedWorlds()               — themed-world catalogue for the home picker.
//   joinThemedWorld(theme)           — one-click create/reuse party in the themed world.
//   fetchWorldTicker()               — cross-world chronicle ticker for the home screen.

import { getJson, postJson, putJson } from './http.js';
import type { HexPatch, HexRow, WorldFull, WorldSummary } from './types.js';

export interface ThemedWorldSummary {
  world_id: number;
  theme: string;
  name: string;
  excerpt: string;
  grid_width: number;
  grid_height: number;
  stats?: { parties_active_7d: number; places_discovered: number; laws_count: number };
  headlines?: string[];
}

export const myWorlds = (): Promise<{ worlds: WorldSummary[] }> => getJson('/api/worlds/me');

export const listWorlds = (): Promise<{ worlds: Array<WorldSummary & { created_by: number }> }> =>
  getJson('/api/worlds');

export const getWorld = (id: number): Promise<{ world: WorldFull; hexes: HexRow[] }> =>
  getJson(`/api/worlds/${id}`);

export const createWorld = (body: {
  name: string;
  setting: string;
  description: string;
  grid_width: number;
  grid_height: number;
}) => postJson<{ world_id: number }>('/api/worlds', body);

export const bulkUpdateHexes = (worldId: number, hexes: HexPatch[]) =>
  putJson<{ updated: number }>(`/api/worlds/${worldId}/hexes/bulk`, { hexes });

export const listThemedWorlds = (): Promise<{ themes: ThemedWorldSummary[] }> =>
  getJson('/api/worlds/themed');

export const joinThemedWorld = (theme: string) =>
  postJson<{ party_id: number; world_id: number; created: boolean }>(`/api/worlds/themed/${encodeURIComponent(theme)}/join`, {});

export interface HexHistoryEvent {
  id: number;
  type: string;
  created_at: string;
  actor_id: number | null;
  summary: string;
  details: unknown;
}

export interface HexHistoryResponse {
  hex_id: number;
  world_id: number;
  events: HexHistoryEvent[];
  narrative: string;
}

export const getHexHistory = (worldId: number, hexId: number, limit = 50): Promise<HexHistoryResponse> =>
  getJson(`/api/worlds/${worldId}/hexes/${hexId}/history?limit=${limit}`);

export interface TickerEntry {
  world_name: string;
  theme: string;
  kind: string;
  text: string;
  created_at: string;
}

export const fetchWorldTicker = (): Promise<{ entries: TickerEntry[] }> =>
  getJson('/api/worlds/ticker');
